import * as React from "react";
import { Dialog } from "radix-ui";

import {
  DialogPrimitiveContent,
  DialogPrimitiveDescription,
  DialogPrimitiveOverlay,
  DialogPrimitivePortal,
  DialogPrimitiveRoot,
  DialogPrimitiveTitle,
} from "../../ui/ModalPrimitive";

import "./styles.css";
import { cn } from "@/libs/cn";
import { XIcon } from "lucide-react";

interface ModalProps extends React.ComponentProps<typeof DialogPrimitiveRoot> {
  title?: string;
  description?: string;
  className?: string;
}

const ModalPortalContext = React.createContext<HTMLElement | null>(null);

/**
 * Radix Popover/Select/Tooltip content portals to document.body with a fixed
 * z-index of 9999, which always wins over the Modal's own z-index. Any float
 * rendered inside a Modal must portal into the Modal's own content node
 * instead of body, so it stacks with the modal instead of racing against it.
 */
export const useModalPortalContainer = () => React.useContext(ModalPortalContext);

const Modal = ({ title, description, open, onOpenChange, children, className, ...props }: ModalProps) => {
  const [contentNode, setContentNode] = React.useState<HTMLDivElement | null>(null);

  return (
    <DialogPrimitiveRoot open={open} onOpenChange={onOpenChange} {...props}>
      <DialogPrimitivePortal>
        <DialogPrimitiveOverlay className="DialogOverlay">
          <DialogPrimitiveContent
            ref={setContentNode}
            aria-describedby=""
            className={cn(
              "relative my-[10%] bg-white w-[90vw] max-w-125 rounded-md p-8 shadow-(--shadow-6) focus:outline-none data-[state=open]:animate-contentShow",
              className,
            )}
          >
            {title ? (
              <DialogPrimitiveTitle className="m-0 text-2xl font-semibold text-[#1E1E1E]">{title}</DialogPrimitiveTitle>
            ) : (
              <DialogPrimitiveTitle className="sr-only">Diálogo</DialogPrimitiveTitle>
            )}
            {description ? (
              <DialogPrimitiveDescription className="mb-4 font-normal text-[16px] text-[#757575]">{description}</DialogPrimitiveDescription>
            ) : null}
            <Dialog.Close asChild>
              <button
                className="absolute right-5.5 top-5.5 inline-flex size-6.25 appearance-none rounded-full p-1 items-center justify-center text-[#2C2C2C] hover:bg-[#2C2C2C]/5 cursor-pointer outline-none"
                aria-label="Close"
              >
                <XIcon />
              </button>
            </Dialog.Close>
            <ModalPortalContext.Provider value={contentNode}>{children}</ModalPortalContext.Provider>
          </DialogPrimitiveContent>
        </DialogPrimitiveOverlay>
      </DialogPrimitivePortal>
    </DialogPrimitiveRoot>
  );
};

const ModalBody = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>(
  ({ children, className, ...props }, ref) => {
    return (
      <div className={cn("flex flex-col w-full", className)} ref={ref} {...props}>
        {children}
      </div>
    );
  },
);

const ModalFooter = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>(
  ({ children, className, ...props }, ref) => {
    return (
      <div className={cn("w-full flex gap-4 justify-end mt-6", className)} ref={ref} {...props}>
        {children}
      </div>
    );
  },
);

export { Modal, ModalBody, ModalFooter };
